import { FunctionComponent, useState, useEffect, Suspense } from 'react';
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { getCurrency } from 'dibs-intl/exports/units';
import { Link } from 'dibs-elements/exports/Link';
import ArrowUp from 'dibs-icons/exports/legacy/ArrowUp';
import { trackSummaryExpanded } from '../../helpers/checkoutTracking';
import { CheckoutMobileSummaryNewPaneTitle } from './CheckoutMobileSummaryNewPaneTitle';
import { CheckoutMobileSummaryPriceTable } from './CheckoutMobileSummaryPriceTable';
import { CheckoutMobileSection } from '../shared/CheckoutMobileSection';
import { CheckoutMobilePromotion } from '../shared/CheckoutMobilePromotion';
import { CheckoutDutiesMessage } from '../../shared/helpers/CheckoutDutiesMessage';
import { CheckoutMobileSummaryNewPane_viewer$key } from './__generated__/CheckoutMobileSummaryNewPane_viewer.graphql';
import { CheckoutMobileSummaryNewPane_transaction$key } from './__generated__/CheckoutMobileSummaryNewPane_transaction.graphql';
import { CheckoutMobileSummaryNewPane_item$key } from './__generated__/CheckoutMobileSummaryNewPane_item.graphql';
import { CheckoutMobileSummaryNewPane_buyer$key } from './__generated__/CheckoutMobileSummaryNewPane_buyer.graphql';
import { query, QueryType } from 'dibs-customs-messaging/exports/PreloadedCustomsMessaging';

import styles from './styles/CheckoutMobileSummaryNewPane.scss';

type CheckoutMobileSummaryNewPaneProps = {
    isComputing: boolean;
    viewer: CheckoutMobileSummaryNewPane_viewer$key | null;
    transaction: CheckoutMobileSummaryNewPane_transaction$key | null;
    item: CheckoutMobileSummaryNewPane_item$key | null;
    buyer: CheckoutMobileSummaryNewPane_buyer$key | null;
};

export const CheckoutMobileSummaryNewPane: FunctionComponent<
    CheckoutMobileSummaryNewPaneProps
> = props => {
    const { isComputing } = props;
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);

    const viewer = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPane_viewer on Viewer {
                ...CheckoutMobilePromotion_viewer
                ...CheckoutDutiesMessage_viewer
            }
        `,
        props.viewer
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPane_transaction on Transaction {
                ...CheckoutMobileSummaryNewPaneTitle_transaction
                ...CheckoutSummaryItemSkuDetails_transaction
                ...CheckoutMobileSummaryPriceTable_transaction
                ...CheckoutMobilePromotion_transaction
                ...CheckoutDutiesMessage_transaction
                ...CheckoutTotalsDutiesMessage_transaction
                buyerChargeCurrency
                isNegotiable
            }
        `,
        props.transaction
    );

    const item = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPane_item on Item {
                serviceId
                ...CheckoutMobileSummaryNewPaneTitle_item
                ...CheckoutSummaryItemSkuDetails_item
                ...CheckoutMobileSummaryPriceTable_item
                ...CheckoutMobilePromotion_item
            }
        `,
        props.item
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPane_buyer on User {
                serviceId
                addressBook {
                    addressList {
                        serviceId
                        country
                    }
                }
            }
        `,
        props.buyer
    );

    const arrowStyles = classnames(styles.arrow, { [styles.arrowDown]: !isSummaryOpen });

    type ReduxState = {
        transaction?: {
            shippingAddressId?: string | '';
        };
    } | null;
    const shippingAddressId = useSelector<ReduxState, string | undefined>(
        state => state?.transaction?.shippingAddressId || ''
    );

    const shippingAddress = shippingAddressId
        ? (buyer?.addressBook?.addressList || []).find(
              address => address?.serviceId === shippingAddressId
          )
        : undefined;

    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const currency = transaction?.buyerChargeCurrency || defaultCurrency;

    function handleClick(): void {
        trackSummaryExpanded(!isSummaryOpen);
        setIsSummaryOpen(!isSummaryOpen);
    }

    const [queryReference, loadQuery] = useQueryLoader<QueryType>(query);
    const userId = buyer?.serviceId;
    const itemId = item?.serviceId;
    const destinationCountry = shippingAddress?.country;

    useEffect(() => {
        if (userId && itemId && destinationCountry) {
            loadQuery({
                userId,
                itemId,
                destinationCountry,
            });
        }
    }, [userId, itemId, loadQuery, destinationCountry]);

    return (
        <>
            <CheckoutMobileSection className={styles.pane}>
                <Link className={styles.trigger} onClick={handleClick}>
                    <CheckoutMobileSummaryNewPaneTitle
                        item={item}
                        transaction={transaction}
                        isExpanded={isSummaryOpen}
                    />
                    <ArrowUp className={arrowStyles} />
                </Link>
                {isSummaryOpen && transaction && item && (
                    <div className={styles.content}>
                        <CheckoutMobileSummaryPriceTable
                            transaction={transaction}
                            item={item}
                            isComputing={isComputing}
                        />
                        <Suspense>
                            <CheckoutDutiesMessage
                                viewer={viewer}
                                isComputing={isComputing}
                                transaction={transaction}
                                currency={currency}
                                isMobile
                                queryReference={queryReference || null}
                            />
                        </Suspense>
                        <CheckoutMobilePromotion
                            viewer={viewer}
                            item={item}
                            transaction={transaction}
                            isComputing={isComputing}
                            trackingSource="Order Summary"
                        />
                    </div>
                )}
            </CheckoutMobileSection>
            {transaction?.isNegotiable && isSummaryOpen && (
                <CheckoutMobileSection className={styles.negotiationDisclaimer}>
                    <FormattedMessage
                        id="checkout.CheckoutMobileSummaryNewPane.negiotationDisclaimer"
                        defaultMessage="You will only be charged if the seller accepts your offer. Offers are valid for 7 days."
                    />
                </CheckoutMobileSection>
            )}
        </>
    );
};
